import type { NavigationItem } from '@af/design-system-docs-ui/navigation/types';

type Status = NonNullable<NavigationItem['item']['status']>;

export type NavigationDefinition = Omit<NavigationItem, 'item' | 'subitems'> & {
	item: Omit<NavigationItem['item'], 'status'> & { status?: Status | null };
	subitems: NavigationDefinition[];
};

/**
 * Existing WorkspaceInfo fields sourced from package.json by Gatsby.
 */
export type PackageStatuses = {
	slug: string;
	status?: Status | null;
	pages?: { slug: string; status?: Status | null }[] | null;
	subPages?: { id: string; status?: Status | null }[] | null;
};

/**
 * Applies package release statuses to the authored navigation without changing its
 * labels, routes or structure. Uses the same package/page slugs and subPage IDs as
 * page creation; numeric folder prefixes are omitted from subPage URLs.
 *
 * The most specific matching package or page supplies the status. A page without
 * its own status inherits its package's status. Navigation status overrides win;
 * null explicitly hides the badge. Overrides do not propagate to child entries.
 * Only the badge type reaches navigation, excluding header descriptions/actions.
 * Packages omitted by a local partial build have no inherited badge.
 */
export function resolveNavigationStatuses(
	navigation: NavigationDefinition[],
	packages: PackageStatuses[],
): NavigationItem[] {
	const statuses = new Map<string, Status | null | undefined>();
	for (const pkg of packages) {
		statuses.set(`/components/${pkg.slug}`, pkg.status);
		for (const page of pkg.pages ?? []) {
			statuses.set(
				`/components/${page.slug}`,
				page.status !== undefined ? page.status : pkg.status,
			);
		}
		for (const page of pkg.subPages ?? []) {
			statuses.set(
				`/components/${pkg.slug}/${page.id.replace(/^\d+-/, '')}`,
				page.status !== undefined ? page.status : pkg.status,
			);
		}
	}
	const routes = [...statuses.keys()].sort((a, b) => b.length - a.length);
	const resolve = (items: NavigationDefinition[]): NavigationItem[] =>
		items.map(({ item: { status: override, ...item }, subitems, ...rest }) => {
			const route = routes.find((route) => item.to === route || item.to?.startsWith(`${route}/`));
			const status = override !== undefined ? override : route ? statuses.get(route) : undefined;
			return {
				...rest,
				item: { ...item, ...(status ? { status: { type: status.type } } : {}) },
				subitems: resolve(subitems),
			};
		});
	return resolve(navigation);
}
